import React, { useEffect, useState } from 'react';
import { Button, Card, OverlayTrigger, Row, Spinner, Table, Tooltip } from 'react-bootstrap';
import { Pagination } from '../../../shared/components/Pagination';
import { InvoiceService } from '../InvoicesService';
import { downloadFileRequest } from '../../../api/services';
import { INVOICE_LIST_DOWNLOAD_URL } from '../../../utils/Endpoints';
import { CampaignsService } from '../../campaigns/CampaignsService';
import { Campaign } from '../../../model/campaign/Campaign';
import { ImpersonateEye } from '../../../shared/components/ImpersonateEye';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { formatDate, getCorrectEnums, hasAccessRight } from '../../../utils/Functions';
import AutocompleteInput from '../../../shared/components/AutocompleteInput';
import { Company } from '../../../model/company/Company';
import { CompanyService } from '../../company/CompanyService';
import CustomFancybox from '../../../shared/components/Fancybox';
import { IUser } from '../../../model/user/IUser';
import { UserService } from '../../user/UserService';
import { AdminAclEnum } from '../../../enum/AdminAclEnum';
import { useNavigate } from 'react-router';
import { ErrorToast } from '../../../utils/toasters';
import { FiInfo } from 'react-icons/fi';
import { IPackages } from '../../../model/package/IPackage';
import { PackagesService } from '../../packages/PackagesService';
import { IContractInvoiceSchedule } from '../../../model/contract/Contract';
import TableSkeleton, { ITableColumn } from '../../../shared/components/TableSkeleton';
import ToggleChip from '../../../shared/components/ToggleChip';
import SingleSelectDropdown, { toSelectValue } from '../../../shared/components/SingleSelectDropdown';

const LONG_TERM_INVOICE_COLUMNS: ITableColumn[] = [
    { label: 'Company' },
    { label: 'Campaign name' },
    { label: 'Package' },
    { label: 'Influencer name' },
    { label: 'Agency name' },
    { label: 'PO number' },
    { label: 'Contract ID' },
    { label: 'Invoice Position' },
    { label: 'Influencer payout' },
    { label: 'Invoice uploaded' },
    { label: 'Under discussion?' },
    { label: 'Invoice' },
    { label: 'Payout deadline' },
    { label: 'Paid' },
    { label: 'Paid date' },
];

export default function InvoicesListContract() {
    const navigate = useNavigate();

    const [pagination, setPagination] = useState({
        page: 1,
        perPage: 15,
        isPaid: undefined,
        offerStatus: '',
        packageId: undefined,
        campaignId: undefined,
        companyId: undefined,
        hasInvoice: 'true',
        hasInfluencerFee: undefined,
        userId: undefined,
        isRejected: false,
    });
    const [totalPages, setTotalPages] = useState(0);
    const [scheduleList, setScheduleList] = useState<IContractInvoiceSchedule[]>([]);
    const [triggerSearch, setTriggerSearch] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);
    const [companyFilter, setCompanyFilter] = useState('');
    const [campaignFilter, setCampaignFilter] = useState('');
    const [influencerFilter, setInfluencerFilter] = useState('');
    const [companyDropdown, setCompanyDropdown] = useState<Company[]>([]);
    const [campaignsDropdown, setCampaignsDropdown] = useState<Campaign[]>([]);
    const [influencerDropdown, setInfluencerDropdown] = useState<IUser[]>([]);
    const [packagesList, setPackagesList] = useState<IPackages[]>([]);

    useEffect(() => {
        CompanyService.getCompanies({ displayNameOrId: companyFilter, perPage: 15 })
            .then(data => {
                setCompanyDropdown(data.data);
            })
            .catch(err => ErrorToast(err));

        PackagesService.getPackages({ page: 1, perPage: 99999999 })
            .then(data => {
                setPackagesList(data.data);
            })
            .catch(err => ErrorToast(err));
    }, [companyFilter]);

    useEffect(() => {
        CampaignsService.getCampaigns({ perPage: 15, title: campaignFilter, isLongTerm: true })
            .then(data => {
                setCampaignsDropdown(data.data);
            })
            .catch(err => ErrorToast(err));
    }, [campaignFilter]);

    useEffect(() => {
        UserService.getUsers({ perPage: 500, userType: 'influencer', displayName: influencerFilter })
            .then(data => {
                setInfluencerDropdown(data.data);
            })
            .catch(err => ErrorToast(err));
    }, [influencerFilter]);

    useEffect(() => {
        setDataLoading(true);
        InvoiceService.getLongTermInvoices({ ...pagination })
            .then(data => {
                setScheduleList(data.data);
                setTotalPages(Math.ceil(data.count / pagination.perPage));
            })
            .catch(err => ErrorToast(err))
            .finally(() => setDataLoading(false));
    }, [triggerSearch]);

    const handlePages = (updatePage: number) => {
        setPagination({ ...pagination, page: updatePage });
        setTriggerSearch(!triggerSearch);
    };

    const handleVerify = (id: number | undefined) => {
        navigate(`/invoices/verify/edit/${id}`);
    };

    function updateInvoiceField(invoiceId: number, field: 'isPaid' | 'isUnderDiscussion') {
        setScheduleList(prev =>
            prev.map(schedule =>
                schedule.invoice.id === invoiceId
                    ? { ...schedule, invoice: { ...schedule.invoice, [field]: !schedule.invoice[field] } }
                    : schedule
            )
        );
    }

    function handleCheckboxPaid(invoiceId: number) {
        InvoiceService.togglePaid(invoiceId)
            .then(() => updateInvoiceField(invoiceId, 'isPaid'))
            .catch(err => ErrorToast(err));
    }

    function handleDiscussionToggle(invoiceId: number) {
        InvoiceService.toggleDiscussion(invoiceId)
            .then(() => updateInvoiceField(invoiceId, 'isUnderDiscussion'))
            .catch(err => ErrorToast(err));
    }

    function handleChange(evt: any) {
        const value = evt.target.value;
        setPagination(prev => ({
            ...prev,
            page: 1,
            [evt.target.name]: value,
        }));
    }

    function handleClick() {
        setPagination(prev => ({
            ...prev,
            page: 1,
        }));
        setTriggerSearch(!triggerSearch);
    }

    return (
        <div>
            <div
                className="card mb-5 p-4 mt-3"
                onKeyDown={e => {
                    if (e.key === 'Enter') handleClick();
                }}
            >
                <Row style={{ rowGap: '18px' }}>
                    <div className="col-md-2 col-6">
                        <AutocompleteInput
                            placeholder="Company name or ID"
                            onSelect={company => {
                                setPagination(prev => ({
                                    ...prev,
                                    companyId: company.id,
                                    page: 1,
                                }));
                                setTriggerSearch(!triggerSearch);
                            }}
                            setFilter={setCompanyFilter}
                            value={companyFilter}
                            items={companyDropdown}
                            hasClearBtn
                            renderItems={['id', 'displayName']}
                            clearFunc={() => {
                                setPagination((prev: any) => ({ ...prev, page: 1, companyId: undefined }));
                                setTriggerSearch(!triggerSearch);
                                setCompanyFilter('');
                            }}
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <AutocompleteInput
                            placeholder="Campaign name or ID"
                            onSelect={campaign => {
                                setPagination(prev => ({
                                    ...prev,
                                    campaignId: campaign.id,
                                    page: 1,
                                }));
                                setTriggerSearch(!triggerSearch);
                            }}
                            setFilter={setCampaignFilter}
                            value={campaignFilter}
                            items={campaignsDropdown}
                            hasClearBtn
                            renderItems={['id', 'title']}
                            clearFunc={() => {
                                setPagination((prev: any) => ({ ...prev, page: 1, campaignId: undefined }));
                                setTriggerSearch(!triggerSearch);
                                setCampaignFilter('');
                            }}
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <SingleSelectDropdown
                            placeholder="All Packages"
                            options={packagesList.map(item => ({
                                value: String(item.id),
                                label: getCorrectEnums(item.displayName),
                            }))}
                            selected={toSelectValue((pagination as any).packageId, '')}
                            onChange={value => handleChange({ target: { name: 'packageId', value: value || '' } })}
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <SingleSelectDropdown
                            placeholder="Is Invoice uploaded?"
                            options={[
                                { value: 'true', label: 'Invoice uploaded' },
                                { value: 'false', label: 'Invoice not uploaded' },
                            ]}
                            selected={toSelectValue((pagination as any).hasInvoice, 'undefined')}
                            onChange={value =>
                                handleChange({ target: { name: 'hasInvoice', value: value || 'undefined' } })
                            }
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <SingleSelectDropdown
                            placeholder="is Invoice paid?"
                            options={[
                                { value: 'true', label: 'Paid' },
                                { value: 'false', label: 'Not Paid' },
                            ]}
                            selected={toSelectValue((pagination as any).isPaid, 'undefined')}
                            onChange={value =>
                                handleChange({ target: { name: 'isPaid', value: value || 'undefined' } })
                            }
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <SingleSelectDropdown
                            placeholder="is Invoice verified?"
                            options={[
                                { value: 'true', label: 'Verified' },
                                { value: 'false', label: 'Not Verified' },
                            ]}
                            selected={toSelectValue((pagination as any).isVerified, 'undefined')}
                            onChange={value =>
                                handleChange({ target: { name: 'isVerified', value: value || 'undefined' } })
                            }
                        />
                    </div>
                    <div className="col-md-2 col-6 autocomplete-input">
                        <AutocompleteInput
                            placeholder="Influencer name"
                            onSelect={user => {
                                setPagination(prev => ({
                                    ...prev,
                                    userId: user.id,
                                    page: 1,
                                }));
                                setTriggerSearch(!triggerSearch);
                            }}
                            setFilter={setInfluencerFilter}
                            value={influencerFilter}
                            items={influencerDropdown}
                            hasClearBtn
                            renderItems={['id', 'displayName']}
                            clearFunc={() => {
                                setPagination((prev: any) => ({ ...prev, page: 1, userId: undefined }));
                                setTriggerSearch(!triggerSearch);
                                setInfluencerFilter('');
                            }}
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <SingleSelectDropdown
                            placeholder="Has influencer fee?"
                            options={[
                                { value: 'true', label: 'Influencer fee' },
                                { value: 'false', label: 'No influencer fee' },
                            ]}
                            selected={toSelectValue((pagination as any).hasInfluencerFee, 'undefined')}
                            onChange={value =>
                                handleChange({ target: { name: 'hasInfluencerFee', value: value || 'undefined' } })
                            }
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <SingleSelectDropdown
                            placeholder="Is under discussion?"
                            options={[
                                { value: 'true', label: 'Under discussion' },
                                { value: 'false', label: 'Not under discussion' },
                            ]}
                            selected={toSelectValue((pagination as any).isUnderDiscussion, 'undefined')}
                            onChange={value =>
                                handleChange({ target: { name: 'isUnderDiscussion', value: value || 'undefined' } })
                            }
                        />
                    </div>
                </Row>
                <div className="d-flex gap-4">
                    <Button className="mt-4" disabled={isLoading} onClick={() => handleClick()}>
                        Search
                    </Button>
                    <Button
                        className="mt-4"
                        variant="outline-primary"
                        onClick={() =>
                            downloadFileRequest(
                                INVOICE_LIST_DOWNLOAD_URL,
                                'export-invoices',
                                null,
                                pagination,
                                setIsLoading
                            )
                        }
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                Generating excel...{' '}
                                <Spinner animation="border" size="sm" role="status" className="ml-1" />
                            </>
                        ) : (
                            'Export current'
                        )}
                    </Button>
                </div>
            </div>
            <Card className="mb-5 p-4 mt-5">
                {dataLoading ? (
                    <TableSkeleton columns={LONG_TERM_INVOICE_COLUMNS} rows={pagination.perPage} />
                ) : (
                    <Table hover responsive>
                        <thead>
                            <tr>
                                {LONG_TERM_INVOICE_COLUMNS.map(column => (
                                    <th key={column.label} className={column.className}>
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {scheduleList.map(item => (
                                <tr key={item.id}>
                                    <td>{item?.contract?.campaign?.company?.displayName}</td>
                                    <td>
                                        <span className="text-primary">({item?.contract?.campaign?.id})</span>
                                        {item?.contract?.campaign?.title}
                                    </td>
                                    <td>{item?.contract?.campaign?.company?.package?.displayName}</td>
                                    <td>
                                        <ImpersonateEye id={item?.contract?.user.id ?? 0} />
                                        {item?.contract?.user?.firstName} {item?.contract?.user?.lastName}
                                    </td>
                                    <td>
                                        {item?.contract?.user?.agencyCompany?.displayName ? (
                                            <span className="word-break">
                                                {item?.contract?.user?.agencyCompany?.displayName}
                                            </span>
                                        ) : (
                                            <span className="word-break text-danger">
                                                <FaTimes />
                                            </span>
                                        )}
                                    </td>
                                    <td>{item?.contract?.purchaseOrderInfluencer ?? '-'}</td>
                                    <td>{item?.contract?.id}</td>
                                    <td>
                                        {item?.sequenceNumber}/{item?.contract?.possibleInvoices}
                                    </td>
                                    <td>{item?.price}€</td>
                                    <td>
                                        {item?.invoice?.id ? (
                                            <span className="word-break text-primary">
                                                <FaCheck />
                                                {formatDate(item?.invoice?.date)}
                                            </span>
                                        ) : (
                                            <span className="word-break text-danger">
                                                <FaTimes />
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        {item.invoice?.id ? (
                                            <ToggleChip
                                                name="isUnderDiscussion"
                                                disabled={!hasAccessRight(AdminAclEnum.INVOICE_ACTION)}
                                                onChange={() => handleDiscussionToggle(item?.invoice?.id ?? 0)}
                                                checked={!!item?.invoice?.isUnderDiscussion}
                                                label={item?.invoice?.isUnderDiscussion ? 'Yes' : 'No'}
                                            />
                                        ) : (
                                            ''
                                        )}
                                    </td>
                                    <td>
                                        {item?.invoice?.id && item?.invoice?.isVerified ? (
                                            <CustomFancybox files={[item?.invoice]} isView />
                                        ) : item?.invoice?.id && !item?.invoice?.isVerified ? (
                                            <Button
                                                type="submit"
                                                onClick={() => handleVerify(item?.invoice?.id)}
                                                disabled={!hasAccessRight(AdminAclEnum.INVOICE_VERIFICATION)}
                                            >
                                                Verify
                                            </Button>
                                        ) : (
                                            <span className="word-break text-danger">
                                                <FaTimes />
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        {item?.contract?.paymentDeadlineDescription ? (
                                            <OverlayTrigger
                                                trigger="click"
                                                rootClose
                                                placement="top"
                                                overlay={
                                                    <Tooltip id={`payment-deadline-tooltip-${item.id}`}>
                                                        {item?.contract?.paymentDeadlineDescription}
                                                    </Tooltip>
                                                }
                                            >
                                                <span>
                                                    <FiInfo size={16} className="cursor-pointer" />
                                                </span>
                                            </OverlayTrigger>
                                        ) : item?.contract?.paymentDeadline ? (
                                            formatDate(item?.contract?.paymentDeadline)
                                        ) : (
                                            <span className="word-break text-danger">
                                                <FaTimes />
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        {item.invoice?.isVerified ? (
                                            <ToggleChip
                                                name="isPaid"
                                                disabled={!hasAccessRight(AdminAclEnum.INVOICE_ACTION)}
                                                onChange={() => handleCheckboxPaid(item?.invoice?.id ?? 0)}
                                                checked={!!item?.invoice?.isPaid}
                                                label={item?.invoice?.isPaid ? 'Paid' : 'Unpaid'}
                                            />
                                        ) : (
                                            ''
                                        )}
                                    </td>
                                    <td>
                                        <div>
                                            {item?.invoice?.isPaid && item?.invoice?.paidAt
                                                ? formatDate(item?.invoice?.paidAt)
                                                : ''}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}

                <Pagination
                    page={pagination.page}
                    totalPages={totalPages}
                    handlePagination={handlePages}
                    disabled={dataLoading}
                />
            </Card>
        </div>
    );
}
